import { isDefined } from "@clipboard-health/util-ts";
import { useExpirableLocalStorage } from "@src/appV2/lib/utils/useExpirableLocalStorage";
import { useEffect, useState } from "react";

const QR_CODE_FIRST_VIEW_TTL = 2 * 60 * 60 * 1000; // 2 hours
const LOCAL_STORAGE_KEY = "touchpoint-qr-first-display-time";

interface UseTouchpointFirstViewTimeProps {
  enabled: boolean;
  shiftId: string;
  clockAction: "clock-in" | "clock-out";
}

/**
 * This hook is used to track the first time a user views the QR code for the Touchpoint integration.
 * @param props.enabled - Whether the QR is visible (view time will be stored only once this becomes true)
 * @param props.shiftId - The ID of the shift
 * @param props.clockAction - The action to be performed (clock-in or clock-out)
 *
 * @returns The first time the user viewed the QR code for the shift and clock action
 * as a timestamp (epoch milliseconds from Date.now()).
 */
export function useTouchpointFirstViewTime(props: UseTouchpointFirstViewTimeProps) {
  const { enabled, shiftId, clockAction } = props;

  const [firstViewTime, setFirstViewTime] = useState<number>();
  const cacheStorageKey = `${shiftId}-${clockAction}`;
  const { getValue: getQrFirstViewTime, setValue: setQrFirstViewTime } =
    useExpirableLocalStorage<number>(LOCAL_STORAGE_KEY);

  useEffect(() => {
    if (enabled && !isDefined(firstViewTime)) {
      const storedTime = getQrFirstViewTime(cacheStorageKey);
      if (isDefined(storedTime)) {
        setFirstViewTime(storedTime);
      } else {
        const now = Date.now();
        setFirstViewTime(now);
        setQrFirstViewTime(cacheStorageKey, now, QR_CODE_FIRST_VIEW_TTL);
      }
    } else if (!enabled) {
      setFirstViewTime(undefined);
    }
  }, [enabled, firstViewTime, cacheStorageKey, getQrFirstViewTime, setQrFirstViewTime]);

  return firstViewTime;
}
